import React from 'react'
import { formatCurrency } from '../../../helpers'
import { Container, Row, Col, Card } from 'react-bootstrap'
import zenscroll from 'zenscroll'
import { PrimaryLink } from 'components/styledComponents/link'
import OlyLink from 'components/olyLink'
import { AccordionSection, AccordionCard } from '../../accordion'
import ContactUsDialog from '../../dialog/contactUsDialog'
import Tooltip from '../../tooltip'
import { Icon } from 'icons'

const FinancialInclusionSection = ({
  data: {
    financeAmount,
    compInsurance,
    priveawayPrice,
    compInsurancePremium,
    leaseProtectionPremium,
    rpcPremium,
    rpcProvider,
    extendedWarrantyPremium,
    gapCoverPremium,
    annualRate,
    leaseTermYears,
    leaseProtection,
    rpc,
    extendedWarranty,
    extendedWarrantyProvider,
    gapCover,
    brand,
    consultantFirstName,
    userFirstName,
    stampDutyViaDisbursement,
    financierEstablishmentFee,
    negativeEquityAmount,
    displayInterest,
    extendedWarrantyTerm,
    compInsurancePremiumFinanced,
    compInsuranceProvider,
  },
  applicationRole,
  expandAccordion,
}) => {
  const getAddOnTerm = () => {
    return extendedWarrantyTerm && extendedWarrantyTerm > 0
      ? extendedWarrantyTerm > 1
        ? '- ' + extendedWarrantyTerm + ' years'
        : '- ' + extendedWarrantyTerm + ' year'
      : ''
  }
  let hasFirstYearComprehensiveInsurance = false
  let financialInclusionAccordionTitle =
    compInsurancePremiumFinanced && compInsurancePremium > 0
      ? 'Includes the car, first year comprehensive insurance and add-on insurances to protect the lease itself.'
      : 'Includes the car, add-on insurances to protect the lease itself.'
  const pacVal = 'pac'
  const rpcProviderlower = rpcProvider && rpcProvider.toLowerCase()
  const extendedWarrantyProviderlower =
    extendedWarrantyProvider && extendedWarrantyProvider.toLowerCase()
  const isPacInsurer =
    rpcProviderlower === pacVal || extendedWarrantyProviderlower === pacVal
  const leaseProtectionInsurance = 'Lease Protection Insurance'
  const lpaTooltip = isPacInsurer
    ? 'When life throws up those big challenges, like losing your job unexpectedly, Lease Protection Insurance is there to assist with not only covering your lease payments, but the costs associated with running your car as well. Life can be unpredictable, so it’s nice to know that in the event of an involuntary redundancy, your novated lease is one thing you won’t need to worry about.'
    : 'Cover your lease repayments if you become involuntarily unemployed.'
  const totalLossAssist = isPacInsurer
    ? 'Total Loss Assist Insurance'
    : 'Total Loss Assist'
  const tLatooltip = isPacInsurer
    ? 'Financial assistance if your vehicle is deemed a total loss by your comprehensive insurer. Total Loss Assist covers the difference between the comprehensive insurance payout and the greater of either the original price, or the amount needed to discharge the lease. Providing you with greater protection in the event your vehicle is written off.'
    : 'Cover in addition to your Comprehensive Motor Vehicle insurance payout if your car is written off. Covers the difference between your comprehensive insurer’s payout amount and either the amount you originally paid for the vehicle or the amount needed to settle your finance contract. The minimum payout for any claim is $5000.'
  const safeGuardorExtended =
    extendedWarrantyProviderlower === pacVal
      ? `Extended Warranty Insurance  ${getAddOnTerm()}`
      : 'Safeguard Ultimate Warranty'
  const extendedTooltip =
    extendedWarrantyProviderlower === pacVal
      ? 'The Extended Warranty is designed to reduce the financial impact of unexpected mechanical or electrical failure by providing cover for most components and parts as covered by the original manufacturer’s warranty. Drive with confidence knowing you’re prepared for almost anything that the road could throw at you.'
      : 'Protect yourself by extending your Manufacturer’s Warranty. This covers parts and labour on components when you hit unexpected mechanical or electrical failure.'
  const extendedOneLiner =
    extendedWarrantyProviderlower === pacVal
      ? 'Cover for unexpected mechanical or electrical failure to most components.'
      : `Extend the car’s factory warranty.`
  const rpcText =
    rpcProviderlower === pacVal
      ? 'Guaranteed Buy Back  Insurance'
      : 'Residual Protection Cover'
  const rpcTooltip =
    rpcProviderlower === pacVal
      ? 'Once your novated lease has reached full term you may decide to sell your vehicle to pay the residual amount owed to the financier. If however the market value of your car is less than the residual amount, there may be a gap payment that you still need to make – GBB provides protection in this scenario and in most circumstances, covers this gap payment for you.'
      : 'Cover in case the trade-in value of your car is less than the ATO discounted value. Provides protection for the difference between the trade-in value of your vehicle and the ATO calculated value at the end of your lease term'
  const rpcOneLiner =
    rpcProviderlower === pacVal
      ? 'Cover in most scenarios if the market value of your car is less than the residual amount at the end of your full lease term.'
      : 'Cover in case the car’s market value is less than the residual.'
  const learnMoreURL = 'https://oly.com.au/novated-lease/insurance/'
    

  const InitFinancialInclusionAccordionTitle = (
    compInsurance,
    leaseProtection,
    rpc,
    extendedWarranty,
    gapCover,
    compInsurancePremiumFinanced
  ) => {
    if (
      compInsurance &&
      compInsurance.length > 0 &&
      compInsurance.toString().toLowerCase() !== 'no' &&
      compInsurancePremiumFinanced
    ) {
      hasFirstYearComprehensiveInsurance = true
      financialInclusionAccordionTitle =
        'Includes the car, first year comprehensive insurance and add-on insurances to protect the lease itself. '
    } else {
      hasFirstYearComprehensiveInsurance = false
      financialInclusionAccordionTitle =
        'Includes the car and add-on insurances to protect the lease itself. '
    }
  }

  InitFinancialInclusionAccordionTitle(
    compInsurance,
    leaseProtection,
    rpc,
    extendedWarranty,
    gapCover,
    compInsurancePremiumFinanced
  )

  const isConsultant = applicationRole.toLowerCase() === 'consultant'

  const scrollTo = (id) => {
    var about = document.getElementById(id)
    zenscroll.to(about)
  }

  return (
    <Container className="section mt-5 mb-5 financialInclusion print-page-break-before">
      {financeAmount > 0 && (
        <div>
          <h4 className="text-dark mb-2">
            Let's talk finance and add-on insurance inclusions
          </h4>
          <p className="font-color-indicative mb-5">
            Here’s where we dig a little deeper into what your estimated amount
            financed includes. Like the car and add-on lease insurances.
          </p>

          <Card className="mb-13rem card-shadow">
            <Card.Header className="border-bottom-0 d-flex justify-content-between">
              <div className="text-header text-uppercase">
                Your total amount financed
              </div>
              <div className="text-larger font-weight-bold">
                {formatCurrency(financeAmount)}
              </div>
            </Card.Header>
            <Card.Body>
              <AccordionSection
                readerText="Your total amount financed"
                expandAccordion={expandAccordion}
                title={financialInclusionAccordionTitle}
              >
                <div className="mb-3">
                  <Row>
                    <Col className="font-weight-bold">Car finance</Col>
                  </Row>
                  {priveawayPrice > 0 && (
                    <Row>
                      <Col
                        className="col--nobold font-color-indicative"
                        lg={9}
                        xs={7}
                      >
                        {brand} driveaway price <br />{' '}
                        <span className="text-small">
                          Includes the car, first year registration, stamp duty
                          & CTP if applicable.
                        </span>
                      </Col>
                      <Col className="text-right" lg={3} xs={5}>
                        {formatCurrency(priveawayPrice)}
                      </Col>
                    </Row>
                  )}

                  {stampDutyViaDisbursement > 0 && (
                    <Row>
                      <Col className="col--nobold font-color-indicative">
                        <div className="d-flex ">
                          <span className="mr-2">
                            Stamp duty via disbursement{' '}
                          </span>
                          <Tooltip
                            header="Stamp duty via disbursement"
                            text="Stamp duty is a tax for transferring registration of the car to you, levied by state governments. In Stamp Duty via disbursement, you can include it in your total amount financed. In which case, your financier pays you the Stamp Duty, which you then pass
 onto the regular authority (like an interstate dealer or private sale). "
                          />
                        </div>
                      </Col>
                      <Col className="text-right">
                        {formatCurrency(stampDutyViaDisbursement)}
                      </Col>
                    </Row>
                  )}

                  {negativeEquityAmount > 0 && (
                    <Row>
                      <Col className="col--nobold font-color-indicative">
                        <div className="d-flex ">
                          <span className="mr-2">Negative Equity </span>
                          <Tooltip
                            header="Negative Equity"
                            text="Negative equity applies if the car you’re currently driving is worth less than the payout amount. E.g. You trade in the car for $13,500, but the payout on the lease is $15,000, leaving a gap of $1,500. You can roll this gap amount into the finance on your
 new lease if the financier approves."
                          />{' '}
                        </div>
                      </Col>
                      <Col className="text-right">
                        {formatCurrency(negativeEquityAmount)}
                      </Col>
                    </Row>
                  )}

                  {financierEstablishmentFee > 0 && (
                    <Row>
                      <Col className="col--nobold font-color-indicative">
                        <div className="d-flex ">
                          <span className="mr-2">
                            Financier's establishment fee{' '}
                          </span>

                          <Tooltip
                            header="Financier's establishment fee"
                            text="A one-time fee for setting up your lease with the financier."
                          />
                        </div>
                      </Col>
                      <Col className="text-right">
                        {formatCurrency(financierEstablishmentFee)}
                      </Col>
                    </Row>
                  )}

                  {compInsurancePremium > 0 && compInsurancePremiumFinanced && (
                    <Row>
                      <Col
                        id="renewal_link"
                        className="col--nobold font-color-indicative"
                        lg={9}
                        xs={7}
                      >
                        1st year Comprehensive Insurance ({brand} arranged)
                        &nbsp;
                        <PrimaryLink
                          onClick={() => scrollTo('renewal_description')}
                        >
                          ^
                        </PrimaryLink>
                        <div className="text-small">
                          {compInsuranceProvider === 'Allianz' && (
                            <>
                              You have a choice of repairer. Please refer to your PDS for details about choice of repairer.&nbsp;
                            </>
                          )}
                          Chat to your consultant if you’d like to arrange this
                          yourself.
                        </div>
                      </Col>
                      <Col className="text-right" lg={3} xs={5}>
                        {formatCurrency(compInsurancePremium)}
                      </Col>
                    </Row>
                  )}
                </div>
                <div className="mb-3">
                  {(rpcPremium > 0 ||
                    leaseProtectionPremium > 0 ||
                    extendedWarrantyPremium > 0 ||
                    gapCoverPremium > 0) && (
                    <div>
                      <Row>
                        <Col>
                          <div className="d-flex ">
                            <span className="mr-2">
                              Add-on lease insurances (currently included){' '}
                            </span>
                            <Tooltip
                              header="Add-on lease insurances you’ve chosen"
                              text="The Motor Vehicle Lease Insurance Options Policy includes four different cover options that you may be eligible for and from which you can choose. The products are optional however can’t be added once your lease has begun. Further information has been provided below on each of the covers to help you make an informed decision. You should read the product disclosure statements before making a decision about these products."
                            />{' '}
                          </div>
                        </Col>
                      </Row>
                      {leaseProtectionPremium > 0 && (
                        <>
                          <Row className="mb-2">
                            <Col className="pl-0 col--nobold" lg={9} xs={7}>
                              <div className="ui-list-a">
                                <span className="ui-list-icon">
                                  <Icon iconType="PinkTick" />
                                </span>
                                <div className="d-flex ">
                                  <span className="mr-2">
                                    {leaseProtectionInsurance}
                                  </span>
                                  <Tooltip
                                    header={leaseProtectionInsurance}
                                    text={lpaTooltip}
                                  />
                                </div>
                              </div>
                            </Col>
                            <Col className="text-right" lg={3} xs={5}>
                              {formatCurrency(leaseProtectionPremium)}
                            </Col>
                          </Row>
                          <Row>
                            <Col className="col--nobold tick-padding">
                              <div className="text-small">
                                Cover your lease repayments if you become
                                involuntarily unemployed.
                              </div>
                              <div className="font-weight-bold">
                                <OlyLink
                                  linkUrl={learnMoreURL + 'lease-protection'}
                                  text="Learn more"
                                  classes="learn-more"
                                />
                              </div>
                            </Col>
                          </Row>
                        </>
                      )}

                      {rpcPremium > 0 && (
                        <>
                          <Row>
                            <Col className="pl-0 col--nobold" xs={8}>
                              <div className="ui-list-a">
                                <span className="ui-list-icon">
                                  <Icon iconType="PinkTick" />
                                </span>
                                <div className="d-flex ">
                                  <span className="mr-2">{rpcText}</span>
                                  <Tooltip header={rpcText} text={rpcTooltip} />
                                </div>
                              </div>
                            </Col>
                            <Col className="text-right" xs={4}>
                              {formatCurrency(rpcPremium)}
                            </Col>
                          </Row>
                          <Row>
                            <Col className="col--nobold tick-padding">
                              <div className="text-small">{rpcOneLiner}</div>
                              <div className="font-weight-bold">
                                <OlyLink
                                  linkUrl={learnMoreURL + 'guaranteed-buy-back'}
                                  text="Learn more"
                                  classes="learn-more"
                                />
                              </div>
                            </Col>
                          </Row>
                        </>
                      )}

                      {gapCoverPremium > 0 && (
                        <>
                          <Row>
                            <Col className="pl-0 col--nobold" xs={8}>
                              <div className="ui-list-a">
                                <span className="ui-list-icon">
                                  <Icon iconType="PinkTick" />
                                </span>
                                <div className="d-flex ">
                                  <span className="mr-2">
                                    {totalLossAssist}{' '}
                                  </span>
                                  <Tooltip
                                    header={totalLossAssist}
                                    text={tLatooltip}
                                  />
                                </div>
                              </div>
                            </Col>
                            <Col className="text-right" xs={4}>
                              {formatCurrency(gapCoverPremium)}
                            </Col>
                          </Row>
                          <Row>
                            <Col className="col--nobold tick-padding">
                              <div className="text-small">
                                Cover in addition to your Comprehensive MV
                                insurance payout if your car is written off.
                              </div>
                              <div className="font-weight-bold">
                                <OlyLink
                                  linkUrl={learnMoreURL + 'total-loss-assist'}
                                  text="Learn more"
                                  classes="learn-more"
                                />
                              </div>
                            </Col>
                          </Row>
                        </>
                      )}

                      {extendedWarrantyPremium > 0 && (
                        <>
                          <Row>
                            <Col className="pl-0 col--nobold" xs={8}>
                              <div className="ui-list-a">
                                <span className="ui-list-icon">
                                  <Icon iconType="PinkTick" />
                                </span>

                                <div className="d-flex ">
                                  <span className="mr-2">
                                    {safeGuardorExtended}{' '}
                                  </span>
                                  <Tooltip
                                    header={safeGuardorExtended}
                                    text={extendedTooltip}
                                  />
                                </div>
                              </div>
                            </Col>
                            <Col className="text-right" xs={4}>
                              {formatCurrency(extendedWarrantyPremium)}
                            </Col>
                          </Row>
                          <Row>
                            <Col className="col--nobold tick-padding">
                              <div className="text-small">
                                {extendedOneLiner}
                              </div>
                              <div className="font-weight-bold">
                                <OlyLink
                                  linkUrl={learnMoreURL + 'extended-warranty'}
                                  text="Learn more"
                                  classes="learn-more"
                                />
                              </div>
                            </Col>
                          </Row>
                        </>
                      )}
                    </div>
                  )}
                  <Row className="text-right font-weight-bold border-top mx-0 pt-2 mt-3">
                    <Col xs md={9} className="px-0">
                      Total amount financed
                    </Col>
                    <Col xs md={3} className="px-0 text-right--bold">
                      {formatCurrency(financeAmount)}
                    </Col>
                  </Row>
                </div>

                <div className="mb-3">
                  <AccordionCard
                    title="Do I have to include lease insurances?"
                    expandAccordion={expandAccordion}
                  >
                    <p>
                      Comprehensive insurance is the only one you’ll need to
                      include. Though many customers find the add-on insurances
                      useful when unexpected things pop up, like mechanical
                      faults after the factory warranty expires.
                    </p>
                  </AccordionCard>
                </div>

                <div className="mb-3">
                  {hasFirstYearComprehensiveInsurance && (
                    <div>
                      <ContactUsDialog
                        className="link font-weight-bold"
                        theme="link"
                        launchText="Contact your consultant"
                        disabled={isConsultant}
                      />{' '}
                      if you'd like to arrange your comprehensive insurance
                      yourself. Want to make changes to your selection of lease
                      insurances? {consultantFirstName} can help.
                    </div>
                  )}
                </div>
                <div className="mb-3">
                  {!(
                    rpcPremium > 0 &&
                    leaseProtectionPremium > 0 &&
                    extendedWarrantyPremium > 0 &&
                    gapCoverPremium > 0
                  ) && (
                    <>
                      <div className="font-weight-bold mb-2">
                        Add-on lease insurances (currently excluded)
                      </div>
                      <div className="font-color-indicative">
                        You'll want to take the time to review this section,{' '}
                        {userFirstName}.
                      </div>
                    </>
                  )}
                  {(rpcPremium === 0 ||
                    leaseProtectionPremium === 0 ||
                    extendedWarrantyPremium === 0 ||
                    gapCoverPremium === 0) && (
                    <div>
                      <div className="font-color-indicative mb-3">
                        Based on your conversation with your consultant, either
                        you’ve opted out of, or are ineligible for, the
                        following insurances:
                      </div>
                      {extendedWarrantyPremium === 0 && (
                        <>
                          <Row>
                            <Col className="pl-0 col--nobold">
                              <div className="ui-list-a">
                                <span className="ui-list-icon">
                                  <img src="/images/cross-icon.svg" alt="" />
                                </span>
                                <span className="d-flex ">
                                  <span className="mr-2">
                                    {safeGuardorExtended}
                                  </span>
                                  <Tooltip
                                    header={safeGuardorExtended}
                                    text={extendedTooltip}
                                  />
                                </span>
                              </div>
                            </Col>
                          </Row>
                          <Row>
                            <Col className="col--nobold tick-padding">
                              <div className="text-small">
                                {extendedOneLiner}
                              </div>
                              <div className="font-weight-bold">
                                <OlyLink
                                  linkUrl={learnMoreURL + 'extended-warranty'}
                                  text="Learn more"
                                  classes="learn-more"
                                />
                              </div>
                            </Col>
                          </Row>
                        </>
                      )}
                      {gapCoverPremium === 0 && (
                        <>
                          <Row>
                            <Col className="pl-0 col--nobold">
                              <div className="ui-list-a">
                                <span className="ui-list-icon">
                                  <img src="/images/cross-icon.svg" alt="" />
                                </span>
                                <span className="d-flex ">
                                  <span className="mr-2">
                                    {totalLossAssist}
                                  </span>
                                  <Tooltip
                                    header={totalLossAssist}
                                    text={tLatooltip}
                                  />
                                </span>
                              </div>
                            </Col>
                          </Row>
                          <Row>
                            <Col className="col--nobold tick-padding">
                              <div className="text-small">
                                Cover in addition to your Comprehensive MV
                                insurance payout if your car is written off.
                              </div>
                              <div className="font-weight-bold">
                                <OlyLink
                                  linkUrl={learnMoreURL + 'total-loss-assist'}
                                  text="Learn more"
                                  classes="learn-more"
                                />
                              </div>
                            </Col>
                          </Row>
                        </>
                      )}
                      {rpcPremium === 0 && (
                        <>
                          <Row>
                            <Col className="pl-0 col--nobold">
                              <div className="ui-list-a">
                                <span className="ui-list-icon">
                                  <img src="/images/cross-icon.svg" alt="" />
                                </span>
                                <span className="d-flex ">
                                  <span className="mr-2">{rpcText}</span>
                                  <Tooltip header={rpcText} text={rpcTooltip} />
                                </span>
                              </div>
                            </Col>
                          </Row>
                          <Row>
                            <Col className="col--nobold tick-padding">
                              <div className="text-small">{rpcOneLiner}</div>
                              <div className="font-weight-bold">
                                <OlyLink
                                  linkUrl={learnMoreURL + 'guaranteed-buy-back'}
                                  text="Learn more"
                                  classes="learn-more"
                                />
                              </div>
                            </Col>
                          </Row>
                        </>
                      )}
                      {leaseProtectionPremium === 0 && (
                        <>
                          <Row>
                            <Col className="pl-0 col--nobold">
                              <div className="ui-list-a">
                                <span className="ui-list-icon">
                                  <img src="/images/cross-icon.svg" alt="" />
                                </span>
                                <span className="d-flex ">
                                  <span className="mr-2">
                                    {leaseProtectionInsurance}
                                  </span>
                                  <Tooltip
                                    header={leaseProtectionInsurance}
                                    text={lpaTooltip}
                                  />
                                </span>
                              </div>
                            </Col>
                          </Row>
                          <Row>
                            <Col className="col--nobold tick-padding">
                              <div className="text-small">
                                Cover your lease repayments if you become
                                involuntarily unemployed.
                              </div>
                              <div className="font-weight-bold">
                                <OlyLink
                                  linkUrl={learnMoreURL + 'lease-protection'}
                                  text="Learn more"
                                  classes="learn-more"
                                />
                              </div>
                            </Col>
                          </Row>
                        </>
                      )}
                    </div>
                  )}
                  <hr></hr>
                  <div className="font-color-indicative mb-3">
                    Understanding the{' '}
                    <a
                      href={learnMoreURL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      full benefits and conditions
                    </a>{' '}
                    of the insurances you choose to include or exclude with your
                    lease is important. Then choose which insurance options are
                    best suited to your circumstances as you won't be able to
                    take up additional insurance options once your lease has
                    begun.
                  </div>
                  <div className="text-small mb-3">
                    {compInsurancePremium > 0 && compInsurancePremiumFinanced && (
                      <div>
                          <PrimaryLink
                          id="renewal_description"
                          onClick={() => scrollTo('renewal_link')}
                        >
                          ^
                        </PrimaryLink>
                        &nbsp; Annual renewals are covered by your running costs.
                      </div>
                    )}
                    {displayInterest && (
                      <>
                        {' '}
                        Remember, you’ll pay this amount back with {annualRate}%
                        interest.
                      </>
                    )}
                  </div>
                  <div className="text-small">
                    Disclaimer: please read the{' '}
                    <OlyLink
                      linkUrl={'https://oly.com.au/novated-lease/insurance#pds'}
                      text="product disclosure statements"
                    />{' '}
                    before making a decision about the insurance products.
                  </div>
                </div>
                <br />
                <div className="print-page-break-before">
                  <AccordionCard
                    title="Why would I want insurances on my lease?"
                    expandAccordion={expandAccordion}
                  >
                    <p>
                      Because there’s bound to be a few unexpected turns in the
                      next {leaseTermYears} years {userFirstName}. Say you lose
                      your job before your lease is up? Lease Protection
                      Insurance steps in. What if your car gets written-off by
                      hail damage? Total Loss Assist has your back.
                      <br />
                      <br />
                      By choosing to include insurances on your lease, you could
                      protect yourself from those incidents out of your control.
                    </p>
                  </AccordionCard>
                </div>
              </AccordionSection>
            </Card.Body>
          </Card>
        </div>
      )}
    </Container>
  )
}

export default FinancialInclusionSection
